import {
  getPendingDemoClaimResumePath,
  peekPendingAuthRedirect,
} from "../utils/demoEntryAnalysisSession";

export { isSessionAuthFailure } from "./sessionAuthFailure";

/** Dispatched when the backend rejects the session or Clerk auth is no longer valid. */
export const SESSION_EXPIRED_EVENT = "tessera:session-expired";

export type SessionExpiredReason = "expired" | "signed_in_elsewhere";

export type SessionExpiredEventDetail = {
  reason?: SessionExpiredReason;
};

export function dispatchSessionExpired(detail?: SessionExpiredEventDetail): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent<SessionExpiredEventDetail>(SESSION_EXPIRED_EVENT, { detail }));
}

/** When Clerk still shows a user but the API rejects the token, treat as a concurrent sign-in. */
export function inferSessionExpiredReason(): SessionExpiredReason {
  if (typeof window === "undefined") return "expired";
  const clerk = (window as { Clerk?: { user?: unknown; session?: unknown } }).Clerk;
  if (clerk?.user && clerk?.session) return "signed_in_elsewhere";
  return "expired";
}

/** Sign-in path with optional return URL (must be same-origin path). */
export function buildSignInRedirectPath(returnPath?: string): string {
  const path =
    returnPath ??
    (typeof window !== "undefined"
      ? window.location.pathname + window.location.search + window.location.hash
      : "");
  if (!path || path === "/") return "/auth/signin";
  return `/auth/signin?redirect_url=${encodeURIComponent(path)}`;
}

function isSafeSameOriginPath(redirectUrl: string): boolean {
  return redirectUrl.startsWith("/") && !redirectUrl.startsWith("//");
}

/**
 * Resolve where to send the user after sign-in / sign-up.
 * Prefers `redirect_url`, then a marked pending path, then an unclaimed demo job resume.
 */
export function resolveSafeRedirectPath(redirectUrl: string | null | undefined): string {
  if (redirectUrl && isSafeSameOriginPath(redirectUrl)) {
    return redirectUrl;
  }

  const pending = peekPendingAuthRedirect();
  if (pending && isSafeSameOriginPath(pending)) {
    return pending;
  }

  const demoResume = getPendingDemoClaimResumePath();
  if (demoResume) {
    return demoResume;
  }

  return "/dashboard";
}
